import { useEffect, useState, useLayoutEffect } from 'react';
import React from 'react';
import { Column, Row } from 'react-foundation';
import * as ArticlesAPI from '../../../api/ArticlesAPI';
import * as FollowsListAPI from '../../../api/FollowsListAPI';
import * as opportunitiesAPI from '../../../api/opportunitiesAPI';
import * as organizationsAPI from '../../../api/organizationsAPI';
import { NewFollowsListPopup } from '../shared/NewFollowsListPopup';
import { SubscribePopup } from '../shared/SubscribePopup';
import { RecommendedOrganizationCard } from './RecommendedOrganizationCard';
import { postFile } from '../../../api/organizationsAPI';
import { configCloudinaryWidget } from '../articles/NewArticle';

// Tabs
import { OrganizationOverviewTab } from './tabs/OrganizationOverviewTab';
import { OrganizationOpportunitesTab } from './tabs/OrganizationOpportunitesTab';
import { OrganizationNewsTab } from './tabs/OrganizationNewsTab';
import { OrganizationGalleryTab } from './tabs/OrganizationGalleryTab';
import { OrganizationVisitsTab } from './tabs/OrganizationVisitsTab';

import Modal from '../shared/Modal';
import WhiteLoadingSpinner from '../../../../assets/images/loading-spinner-white.svg';
import PurpleLoadingSpinner from '../../../../assets/images/loading-spinner-purple.svg';

// // Assets
import { FilePreviewer } from '../shared/FilePreviewer';
import EARTH_ICON from '../../../../assets/images/org-earth-icon.svg';
import SOCIAL_MEDIA_ICON from '../../../../assets/images/org-social-media-icon.svg';
import AUDIENCE_ICON from '../../../../assets/images/org-audience-size-icon.svg';
import AGES_ICON from '../../../../assets/images/org-ages-icon.svg';
import CULTURAL_INFLUENCE_ICON from '../../../../assets/images/org-cultural-influence-icon.svg';
import GENDER_ICON from '../../../../assets/images/org-gender-icon.svg';
import ORGANIZATION_IMG_DEFAULT from '../../../../assets/images/default-organization.png';
import { Helmet } from 'react-helmet';
import { replaceMetaTag, replaceTitleFromHead } from '../../utils';

/**
 * Component for organization profile
 * @param orgID the organization ID
 * @param currentUser the current user object
 * @param kind the kind of lookup the user did to get to this page
 * @param previousOrg the id of the previous org (only used if arrows are present from specific types of kind)
 * @param nextOrg the id of the next org (only used if arrows are present from specific types of kind)
 * @param currentUserCanEdit whether or not the current user can edit (true for yes, false for no)
 * @param maskContent whether or not to mask the content (this is used to determine if someone is subscribed or not
 */
const Organization = ({
	orgID,
	currentUser,
	kind,
	previousOrg,
	nextOrg,
	currentUserCanEdit,
	maskContent,
	organization_visits,
	recommended_organizations,
}) => {
	const orgTemplate = {
		address_line_1: '',
		address_line_2: '',
		admin_user_id: '',
		affiliation: '',
		approved: '',
		assets: [],
		audience_age_group: '',
		audience_gender: '',
		audience_size: '',
		city: '',
		country: '',
		created_at: '',
		description: '',
		facebook_followers: '',
		facebook_url: '',
		follows_count: '',
		id: '',
		instagram_followers: false,
		instagram_url: '',
		interest_areas: [],
		lat: '',
		legal_name: '',
		linkedin_followers: '',
		linkedin_url: '',
		lng: '',
		logo: '',
		operating_name: '',
		organization_email: '',
		organization_recommendation_for_users: [],
		organization_type: '',
		other_partners: '',
		partners: [],
		postal_code: '',
		searchable_text: '',
		sponsorship_url: '',
		state: '',
		tiktok_followers: '',
		tiktok_url: '',
		twitter_followers: '',
		twitter_url: '',
		updated_at: '',
		users_and_contacts: [],
		view_count: '',
		visitor_activity_email_send_date: '',
		website: '',
		youtube_followers: '',
		youtube_url: '',
	};
	/**
	 * ---- Variables/States ----
	 * orgData: Responsible for holding all org data information
	 * opportunities: Responsible for holding all opportunity data for this org
	 * articles: Responsible for holding all article data for this org
	 * tabState: Keeps track of the current tab
	 * followsLists: The followsLists of the current user
	 * showAddFollowPopup: Controls whether the follow popup appears or not (true for yes, false for no)
	 * isApproveLoading: Is there an approve request out right now? True for yes, which means display a loading spinner
	 * showSubscribePopup: Should the subscribe popup be appear?
	 * statusPopupObject: Keeps track of the status popup banner component data
	 * showStatusPopupObject: Responsible for showing and hiding the status popup banner component. True for show, false for hide.
	 * cloudinaryUploadWidget: Holds a reference to the cloudinary upload widget
	 */
	const [orgData, setOrgData] = useState(orgTemplate);
	const [isOrgDataDoneLoading, setIsOrgDataDoneLoading] = useState(false);

	const [opportunities, setOpportunities] = useState([]);
	const [articles, setArticles] = useState([]);
	const [tabState, setTabState] = useState(0);
	const [followsLists, setFollowsLists] = useState([]);
	const [showAddFollowPopup, setShowAddFollowPopup] = useState(false);
	const [isApproveLoading, setIsApproveLoading] = useState(false);
	const [showSubscribePopup, setShowSubscribePopup] = useState(false);
	const [statusPopupObject, setStatusPopupObject] = useState({});
	const [showStatusPopupObject, setShowStatusPopupObject] = useState(false);
	const [cloudinaryUploadWidget, setCloudinaryUploadWidget] =
		useState(undefined);

	const [isFetchingOpportunities, setIsFetchingOpportunities] =
		useState(false);

	// Simply add "VISITS" to the tabs array to add back the visits tab
	const tabs = ['OVERVIEW', 'OPPORTUNITIES', 'NEWS'];

	currentUser ? tabs.push('GALLERY') : null;
	currentUserCanEdit ? tabs.push('VISITS') : null;

	const fakeOrg = {
		legal_name: '??',
	};

	const maxGalleryFiles = 10;

	/**
	 * Do this on component mount, before page load
	 */
	useEffect(() => {
		fetchOrganizationInfo();
		fetchOpportunities();
		fetchArticles();
		fetchFollowsList();
		configCloudinaryWidget(
			setCloudinaryUploadWidget,
			handleSuccessfulFileUpload
		);
		// If user is null, meaning unsubscribed
		if (!currentUser) {
			setTimeout(() => {
				setShowSubscribePopup(true);
			}, 5000);
		}

		let url = window.location.href;
		if (url.includes('attach=true')) {
			let tempShowStatusObject = {
				type: 'success',
				text: 'Successfully connected to organization.',
			};
			setStatusPopupObject(tempShowStatusObject);
			setShowStatusPopupObject(true);
		}

		if (
			url.includes('tab=opportunities') ||
			url.includes('tab=campaigns')
		) {
			setActiveTab(1, 'OPPORTUNITIES-tab-li');
		} else {
			setActiveTab(0, 'OVERVIEW-tab-li');
		}
	}, []);

	useLayoutEffect(() => {
		if (isOrgDataDoneLoading) {
			const metaContent = `Explore sponsorship and partnership opportunities with ${orgData.legal_name} at Sponsor Circle.`;
			replaceMetaTag('description', metaContent);
			replaceTitleFromHead(
				`${orgData.legal_name} Event Partnership Opportunities - Sponsor Circle`
			);
		}
	}, [isOrgDataDoneLoading]);

	/**
	 * Callback function for successful file upload to cloudinary
	 * @param result the result that cloudinary gives us
	 */
	const handleSuccessfulFileUpload = (result) => {
		setShowStatusPopupObject(false);
		postFile(orgID, result.info).then((response) => {
			let tempShowStatusObject;

			if (response.status === 200) {
				setOrgData((orgData) => {
					return {
						...orgData,
						assets: [...orgData.assets, result.info],
					};
				});

				tempShowStatusObject = {
					type: 'success',
					text: 'Image successfully uploaded',
				};
			} else {
				tempShowStatusObject = {
					type: 'failure',
					text: response.message,
				};
			}

			setStatusPopupObject(tempShowStatusObject);
			setShowStatusPopupObject(true);
		});
	};

	/**
	 * Callback function for unsuccessful file upload to cloudinary
	 */
	const handleFailedFileUpload = () => {
		setShowStatusPopupObject(false);
		let tempShowStatusObject = {
			type: 'failure',
			text: 'Something went wrong with the file upload service, please try again.',
		};
		setStatusPopupObject(tempShowStatusObject);
		setShowStatusPopupObject(true);
	};

	// TODO - this should be componentized (also appears in Articles)
	const fetchFollowsList = async () => {
		try {
			let response = await FollowsListAPI.fetchAllLists();

			let tempFollowsList = [];
			for (const list of response.data) {
				let tempList = {
					name: list.name,
					id: list.id,
					organization_ids: [],
				};
				for (const org of list?.organizations) {
					tempList.organization_ids.push(org?.organization?.id);
				}
				tempFollowsList.push(tempList);
			}
			response.data = tempFollowsList;
			setFollowsLists(response.data);
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Fetches opportunities that belong to the current organization
	 */
	const fetchOpportunities = async () => {
		try {
			setIsFetchingOpportunities(true);
			let response = await opportunitiesAPI.getOpportunities(orgID);
			setOpportunities(response.data);
			setIsFetchingOpportunities(false);
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Fetches articles that belong to the current organization (WIP)
	 */
	const fetchArticles = async () => {
		try {
			let response = await ArticlesAPI.getArticlesByOrgId(orgID);
			let articles = response.data;
			setArticles(articles);
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Fetches info about the current organization
	 */
	const fetchOrganizationInfo = async () => {
		try {
			let response = await organizationsAPI.getOrganization(orgID);
			let organization = response.data;
			setOrgData(organization);
			setIsOrgDataDoneLoading(true);

			const followerContainers = [
				'facebook_followers',
				'twitter_followers',
				'youtube_followers',
				'linkedin_followers',
				'instagram_followers',
				'tiktok_followers',
			];
			let totalFollowers = calculateTotalFollowers(organization);
			if (totalFollowers !== 0) {
				for (const followerType of followerContainers) {
					let currentFollowers = organization[followerType]
						? organization[followerType]
						: 0;
					let element = document.getElementById(
						followerType + '-container'
					);
					if (element) {
						element.style.width =
							String(
								Math.max(
									(currentFollowers * 100) / totalFollowers,
									20
								)
							) + '%';
					}
				}
			} else {
				for (const followerType of followerContainers) {
					let element = document.getElementById(
						followerType + '-container'
					);
					if (element) {
						element.style.width = '20%';
					}
				}
			}

			// If current user is not set, this means user is unsubscribed, so make up some information
			if (!currentUser) {
				orgData.users_and_contacts = [
					{
						country: '??',
						email: '??',
						first_name: '??',
						id: 0,
						last_name: '??',
						linked_in_profile: '??',
						organization_id: 0,
						organization_relationship: '??',
						phone_number: '??',
						source: '',
						title: '??',
					},
				];
			}
		} catch (e) {
			console.error(e);
		}
	};

	/**
	 * Handles cancelling of the form, and bringing the user back to the organizations page
	 */
	const handleCancel = () => {
		location.assign(`/organizations`);
	};

	/**
	 * Calculates the total number of followers, and checks for nulls or undefined values
	 * @param dataToUse the dataset to use. if we do not call the function with one, then use org Data
	 * @return {number} the total number
	 */
	const calculateTotalFollowers = (dataToUse) => {
		let totalFollowers = 0;
		let data = dataToUse ? dataToUse : orgData;
		const toAdd = [
			data.facebook_followers,
			data.instagram_followers,
			data.tiktok_followers,
			data.twitter_followers,
			data.youtube_followers,
			data.linkedin_followers,
		];

		for (const followerCount of toAdd) {
			if (followerCount) {
				totalFollowers += parseInt(followerCount);
			}
		}
		return totalFollowers;
	};

	/**
	 * Calculate the percentage of total followers that a social media platform has
	 * @param followerAmount the follower amount to use
	 * @return {number} the percentage number
	 */
	const calculatePercentageOfTotalFollowers = (followerAmount) => {
		if (followerAmount) {
			const percentage = Math.round(
				(followerAmount * 100.0) / calculateTotalFollowers()
			);

			return percentage > 10 ? percentage + '%' : '';
		} else {
			return '';
		}
	};

	/**
	 * Controls which tab is active and what to show
	 */
	const TabContent = () => {
		switch (tabState) {
			case 0:
				return (
					<OrganizationOverviewTab
						orgID={orgID}
						orgData={orgData}
						isSubscribed={!maskContent}
						fakeOrg={fakeOrg}
						currentUser={currentUser}
						currentUserCanEdit={currentUserCanEdit}
						isOrgDataDoneLoading={isOrgDataDoneLoading}
					/>
				);
			case 1:
				return (
					<OrganizationOpportunitesTab
						orgID={orgID}
						orgData={orgData}
						opportunities={opportunities}
						currentUserCanEdit={currentUserCanEdit}
						isFetchingOpportunities={isFetchingOpportunities}
					/>
				);
			case 2:
				return (
					<OrganizationNewsTab
						articles={articles}
						currentUserCanEdit={currentUserCanEdit}
					/>
				);
			case 3:
				return (
					<OrganizationGalleryTab
						orgData={orgData}
						setOrgData={setOrgData}
						currentUserCanEdit={currentUserCanEdit}
						cloudinaryUploadWidget={cloudinaryUploadWidget}
						maxGalleryFiles={maxGalleryFiles}
					/>
				);
			case 4:
				return (
					<OrganizationVisitsTab
						currentUser={currentUser}
						organization_visits={organization_visits}
						orgID={orgID}
					/>
				);
			default:
				return null;
		}
	};

	/**
	 * Redirect the user to the organization's website, in a new tab
	 */
	const redirectToWebsite = () => {
		window.open(orgData.website, '_blank');
	};

	/**
	 * Redirect the user to the proper social media platform, in a new tab
	 * @param socialMediaType the type of social media
	 */
	const redirectToSocialMedia = (socialMediaType) => {
		let URL;
		if (
			orgData[socialMediaType + '_url'].startsWith('https') ||
			orgData[socialMediaType + '_url'].startsWith('http')
		) {
			URL = orgData[socialMediaType + '_url'];
		} else {
			URL = 'https://' + orgData[socialMediaType + '_url'];
		}
		window.open(URL, '_blank');
	};

	/**
	 * Redirect the user to the organizations directory with the current interest area selected as a filter
	 * @param interestAreaID the id of the interest area clicked
	 */
	const redirectToOrganizationsDirectoryWithInterestArea = (
		interestAreaID
	) => {
		window.open('/organizations?interest_area=' + interestAreaID);
	};

	/**
	 * Sets the active tab, and deactives all other active tabs
	 * @param index the index of the new active tab
	 * @param tagID the id of the tab
	 */
	const setActiveTab = (index, tagID) => {
		let currentActiveTabs = document.getElementsByClassName('active-tab');
		if (currentActiveTabs.length > 0) {
			for (const activeTab of currentActiveTabs) {
				activeTab.className = activeTab.className.replace(
					' active-tab',
					''
				);
			}
		}

		document.getElementById(tagID).className += ' active-tab';
		setTabState(index);
	};

	/**
	 * Handles clicking of the follow/unfollow button and bringing up the follows list popup
	 */
	const handleFollowOrg = () => {
		if (currentUser) {
			setShowAddFollowPopup(!showAddFollowPopup);
		} else {
			location.assign('/users/sign_up');
		}
	};

	/**
	 * Checks whether the org is currently followed
	 * @return {boolean} true if followed, false if not
	 */
	const isOrgFollowed = () => {
		for (const list of followsLists) {
			if (list.organization_ids?.includes(orgID)) {
				return true;
			}
		}
		return false;
	};

	/**
	 * Calls api to approve org
	 */
	const approveOrg = () => {
		setIsApproveLoading(true);
		organizationsAPI.approveOrg(orgID).then((response) => {
			let tempShowStatusObject;
			if (response.status === 200) {
				orgData.approved = true;
				setOrgData(orgData);

				tempShowStatusObject = {
					type: 'success',
					text: 'Successfully approved organization.',
				};
			} else {
				tempShowStatusObject = {
					type: 'failure',
					text: 'Failed to approve organization. Please try again.',
				};
			}

			setIsApproveLoading(false);
			setStatusPopupObject(tempShowStatusObject);
			setShowStatusPopupObject(true);
		});
	};

	/**
	 * Calls api to unapprove orgFG
	 */
	const unapproveOrg = () => {
		setIsApproveLoading(true);
		organizationsAPI.unapproveOrg(orgID).then((response) => {
			let tempShowStatusObject;
			if (response.status === 200) {
				orgData.approved = false;
				setOrgData(orgData);

				tempShowStatusObject = {
					type: 'success',
					text: 'Successfully un-approved organization.',
				};
			} else {
				tempShowStatusObject = {
					type: 'failure',
					text: 'Failed to approve organization. Please try again.',
				};
			}

			setIsApproveLoading(false);
			setStatusPopupObject(tempShowStatusObject);
			setShowStatusPopupObject(true);
		});
	};

	/**
	 * Handles redirecting to the previous org
	 */
	const redirectToPreviousOrg = () => {
		let URL = `/organizations/${previousOrg.slug}`;
		if (kind) {
			URL += '?kind=' + kind;
		}
		location.assign(URL);
	};

	/**
	 * Handles redirecting to the next org
	 */
	const redirectToNextOrg = () => {
		let URL = `/organizations/${nextOrg.slug}`;
		if (kind) {
			URL += '?kind=' + kind;
		}
		location.assign(URL);
	};

	const OrganizationCardLogo = () => {
		const [isLogoUploadModalOpen, setIsLogoUploadModalOpen] =
			useState(false);

		const [logo, setLogo] = useState(() => {
			if (orgData?.logo?.url) return orgData?.logo?.url;

			if (orgData?.logo?.path)
				return `https://res.cloudinary.com/sponsor-circle/image/upload/${orgData?.logo?.path}`;

			if (orgData?.assets[0]?.url) return orgData?.assets[0]?.url;

			if (orgData?.assets[0]?.path)
				return `https://res.cloudinary.com/sponsor-circle/image/upload/${orgData?.assets[0]?.path}`;

			return ORGANIZATION_IMG_DEFAULT;
		});

		const UploadLogoButton = () => {
			return (
				<div
					onClick={() => setIsLogoUploadModalOpen(true)}
					className={'open-org-logo'}
				>
					<i className="fa fa-camera"></i>
					Upload logo
				</div>
			);
		};

		const UploadLogoModal = () => {
			return (
				<div>
					<div>Upload a logo for your organization</div>
					<FilePreviewer
						handleApiCall={organizationsAPI.putOrganizationLogo}
						orgData={orgData}
						isMaxLimit={false}
					/>
				</div>
			);
		};

		return (
			<div className="default-organization-wrapper">
				{(currentUser?.admin ||
					currentUser?.organization_id === orgData.id) && (
					<UploadLogoButton />
				)}
				<img
					src={logo}
					alt={`${orgData?.operating_name} Logo`}
					className="org-logo-fit"
				/>
				<Modal
					Content={UploadLogoModal}
					modalState={isLogoUploadModalOpen}
					turnOffModal={() => setIsLogoUploadModalOpen('')}
				/>
				<h1 className="org-name">{orgData.legal_name}</h1>
			</div>
		);
	};

	return (
		<div className="organization-display-area">
			<h1 className="seo-h1-content">{`${orgData.legal_name} - Sponsor Circle`}</h1>
			{previousOrg ? (
				<i
					className="fas fa-chevron-left prev-org"
					onClick={redirectToPreviousOrg}
				/>
			) : (
				''
			)}
			{nextOrg ? (
				<i
					className="fas fa-chevron-right next-org"
					onClick={redirectToNextOrg}
				/>
			) : (
				''
			)}

			{/* {showSubscribePopup ? (
				<SubscribePopup setShow={setShowSubscribePopup} />
			) : (
				''
			)} */}

			{/* {showStatusPopupObject ? (
				<StatusPopup
					statusPopupObject={statusPopupObject}
					setShowStatusPopup={setShowStatusPopupObject}
				/>
			) : (
				''
			)} */}

			<Row className="thinner-horizontal-margins">
				<Column small={12}>
					<div className="back-link" onClick={handleCancel}>
						<i className="fas fa-chevron-left" />
						<p className="button-text">Back to organizations</p>
					</div>
				</Column>
				<div className="bottom-70px-margin" />
			</Row>

			{showAddFollowPopup ? (
				<NewFollowsListPopup
					followsListsParam={followsLists}
					setShow={setShowAddFollowPopup}
					orgID={orgID}
					currentUser={currentUser}
					setShowStatusPopup={setStatusPopupObject}
					setShowStatus={setShowStatusPopupObject}
				/>
			) : (
				''
			)}

			<Row className="thinner-horizontal-margins">
				<Column small={8} medium={4} className="organizations-column">
					<div className="card">
						{currentUser &&
							currentUser?.organization_id !== orgID &&
							(isOrgFollowed() ? (
								<div
									className="primary-button follow-button float-right"
									onClick={handleFollowOrg}
								>
									<p>Following</p>
								</div>
							) : (
								<div
									className="secondary-button follow-button float-right"
									onClick={handleFollowOrg}
								>
									<p>Follow</p>
								</div>
							))}

						{!isOrgDataDoneLoading ? (
							<Column>
								{' '}
								<div style={{ textAlign: 'center' }}>
									<img
										src={PurpleLoadingSpinner}
										alt="Loading spinner"
									/>
								</div>{' '}
							</Column>
						) : (
							<Column>
								<OrganizationCardLogo />
								<div className="body-font org-info-field">
									{orgData?.website ? (
										<p
											className="website-link"
											onClick={redirectToWebsite}
										>
											{orgData?.website}
										</p>
									) : (
										''
									)}
								</div>
								<div className="org-info-field">
									<label className="body-font info-field-descriptor">
										Following
									</label>
									<i className="fas fa-solid fa-heart" />
									<label className="body-font info-field-value">
										{orgData.follows_count}
									</label>
								</div>

								<div className="org-info-field">
									<label className="body-font info-field-descriptor">
										Views
									</label>
									<i className="fas fa-solid fa-eye" />
									<label className="body-font info-field-value">
										{orgData.view_count}
									</label>
								</div>

								<div className="org-info-field">
									<label className="body-font info-field-descriptor">
										Contacts
									</label>
									<i className="fas fa-user-friends" />
									<label className="body-font info-field-value">
										{orgData.users_and_contacts.length}
									</label>
								</div>

								<div className="org-info-field">
									<label className="body-font info-field-descriptor">
										Location
									</label>
									<i className="fas fa-map-marker-alt" />
									<label className="body-font info-field-value">
										{orgData.city}
									</label>
								</div>
							</Column>
						)}
					</div>

					<div className="interest-and-social-media-card">
						<Row style={{ paddingBottom: '15px' }}>
							<div className="columns small-2 text-right margin-bottom-large">
								<img src={EARTH_ICON} alt="Globe Icon" />
							</div>
							<h6 className="sub-header ">
								Areas of Sponsor Interest
							</h6>
						</Row>

						<div
							id="opportunities-check-box"
							className="check_boxes row"
						>
							<div className="columns small-12 button-group">
								{orgData.interest_areas.map(
									(interestObject) => {
										return (
											<div
												className="tag-button"
												id={interestObject.id}
												key={interestObject.name}
												onClick={() =>
													redirectToOrganizationsDirectoryWithInterestArea(
														interestObject.id
													)
												}
											>
												<label>
													<div
														id={
															'sponsorable-opportunity-' +
															interestObject.id
														}
													>
														<p className="body-font">
															{
																interestObject.name
															}
														</p>
													</div>
												</label>
											</div>
										);
									}
								)}
							</div>
						</div>

						{calculateTotalFollowers() !== 0 && (
							<div>
								<Row style={{ paddingBottom: '15px' }}>
									<div className="columns small-2 text-right">
										<img
											src={SOCIAL_MEDIA_ICON}
											alt="Social Media Icon"
										/>
									</div>
									<div style={{ display: 'flex' }}>
										<h6 className="sub-header">
											Social Media
										</h6>
									</div>
								</Row>
								{orgData.facebook_url ? (
									<div
										className="social-media-followers-container"
										id="facebook_followers-container"
										onClick={() =>
											redirectToSocialMedia('facebook')
										}
									>
										<div className="logo-div">
											<i className="fab fa-facebook" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.facebook_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								{orgData.twitter_url ? (
									<div
										className="social-media-followers-container"
										id="twitter_followers-container"
										onClick={() =>
											redirectToSocialMedia('twitter')
										}
									>
										<div className="logo-div">
											<i className="fab fa-twitter" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.twitter_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								{orgData.instagram_url ? (
									<div
										className="social-media-followers-container"
										id="instagram_followers-container"
										onClick={() =>
											redirectToSocialMedia('instagram')
										}
									>
										<div className="logo-div">
											<i className="fab fa-instagram" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.instagram_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								{orgData.tiktok_url ? (
									<div
										className="social-media-followers-container"
										id="tiktok_followers-container"
										onClick={() =>
											redirectToSocialMedia('tiktok')
										}
									>
										<div className="logo-div">
											<i className="fab fa-tiktok" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.tiktok_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								{orgData.linkedin_url ? (
									<div
										className="social-media-followers-container"
										id="linkedin_followers-container"
										onClick={() =>
											redirectToSocialMedia('linkedin')
										}
									>
										<div className="logo-div">
											<i className="fab fa-linkedin-in" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.linkedin_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								{orgData.youtube_url ? (
									<div
										className="social-media-followers-container"
										id="youtube_followers-container"
										onClick={() =>
											redirectToSocialMedia('youtube')
										}
									>
										<div className="logo-div">
											<i className="fab fa-youtube" />
										</div>
										<p className="body-font font-small">
											{calculatePercentageOfTotalFollowers(
												orgData.youtube_followers
											)}
										</p>
									</div>
								) : (
									''
								)}

								<label className="body-font margin-top-medium">
									Total followers:{' '}
									<strong>
										{calculateTotalFollowers()
											.toString()
											.replace(
												/\B(?=(\d{3})+(?!\d))/g,
												','
											)}
									</strong>
								</label>
							</div>
						)}

						<Audiences orgData={orgData} />
					</div>
				</Column>

				<Column small={16} medium={8} className="organizations-column">
					<div className="tab-content-container">
						<div className="tab-links-container">
							<ul className="tabs" id="dashboard-tabs">
								{tabs.map((tab, index) => {
									if (
										!currentUserCanEdit &&
										tab === 'NEWS' &&
										articles.length === 0
									)
										return;

									return (
										<li
											className="tabs-title"
											id={tab + '-tab-li'}
											key={index}
										>
											<a
												onClick={() =>
													setActiveTab(
														index,
														tab + '-tab-li'
													)
												}
											>
												<p>{tab}</p>
											</a>
										</li>
									);
								})}
							</ul>
						</div>
						<TabContent />
					</div>
				</Column>
			</Row>

			<div className="spacing-div" />

			{currentUser === null ? (
				<div className="other-interest-profiles">
					<h2 className="other-profiles-title">
						Other profiles you might be interested in
					</h2>
					<Row>
						<div className="recommended-profiles-container">
							<Column
								small={8}
								medium={4}
								className="organizations-column blur-contents"
							>
								<RecommendedOrganizationCard
									orgData={fakeOrg}
								/>
							</Column>
							<Column
								small={8}
								medium={4}
								className="organizations-column blur-contents"
							>
								<RecommendedOrganizationCard
									orgData={fakeOrg}
								/>
							</Column>
							<Column
								small={8}
								medium={4}
								className="organizations-column blur-contents"
							>
								<RecommendedOrganizationCard
									orgData={fakeOrg}
								/>
							</Column>
						</div>
					</Row>
				</div>
			) : orgData?.organization_recommendation_for_users?.length > 0 ? (
				<div className="other-interest-profiles">
					<h2 className="other-profiles-title">
						Other profiles you might be interested in
					</h2>
					<Row>
						<div className="recommended-profiles-container">
							{orgData
								.organization_recommendation_for_users[0] ? (
								<Column
									small={8}
									medium={4}
									className="organizations-column"
								>
									<RecommendedOrganizationCard
										orgData={
											orgData
												.organization_recommendation_for_users[0]
										}
									/>
								</Column>
							) : (
								''
							)}

							{orgData
								.organization_recommendation_for_users[1] ? (
								<Column
									small={8}
									medium={4}
									className="organizations-column"
								>
									<RecommendedOrganizationCard
										orgData={
											orgData
												.organization_recommendation_for_users[1]
										}
									/>
								</Column>
							) : (
								''
							)}

							{orgData
								.organization_recommendation_for_users[2] ? (
								<Column
									small={8}
									medium={4}
									className="organizations-column"
								>
									<RecommendedOrganizationCard
										orgData={
											orgData
												.organization_recommendation_for_users[2]
										}
									/>
								</Column>
							) : (
								''
							)}
						</div>
					</Row>
				</div>
			) : (
				''
			)}

			{currentUser?.admin ? (
				<Row className="thinner-horizontal-margins">
					<Column small={12}>
						{orgData?.approved ? (
							<div className="approve-banner-inner">
								<p className="header-font">
									This org is currently approved. Would you
									like to unapprove it?
								</p>
								<div
									className="primary-button"
									onClick={() => unapproveOrg()}
								>
									{isApproveLoading ? (
										<img
											id="file-upload-loading-spinner"
											src={WhiteLoadingSpinner}
											alt="Loading spinner"
										/>
									) : (
										<p>Unapprove</p>
									)}
								</div>
							</div>
						) : (
							<div className="approve-banner-inner">
								<p className="header-font">
									This org is currently unapproved. Would you
									like to approve it?
								</p>
								<div
									className="primary-button"
									onClick={() => approveOrg()}
								>
									{isApproveLoading ? (
										<img
											id="file-upload-loading-spinner"
											src={WhiteLoadingSpinner}
											alt="Loading spinner"
										/>
									) : (
										<p>Approve</p>
									)}
								</div>
							</div>
						)}
					</Column>
				</Row>
			) : (
				''
			)}
		</div>
	);
};

const Audiences = ({ orgData }) => {
	const {
		audience_primary_age_group,
		audience_primary_cultural_influence,
		audience_primary_gender,
		audience_secondary_age_group,
		audience_size,
	} = orgData;

	const AgeGroup = () =>
		audience_primary_age_group && audience_primary_age_group !== '' ? (
			<Row style={{ paddingBottom: '15px', paddingTop: '15px' }}>
				<div className="columns small-2 text-right">
					<img src={AGES_ICON} />
				</div>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<h6 className="sub-header">Ages</h6>
					<div className="qualities-result">
						{audience_primary_age_group}
					</div>
				</div>
			</Row>
		) : null;
	const CulturalInfluence = () =>
		audience_primary_cultural_influence &&
		audience_primary_cultural_influence !== '' ? (
			<Row style={{ paddingBottom: '15px', paddingTop: '15px' }}>
				<div className="columns small-2 text-right">
					<img src={CULTURAL_INFLUENCE_ICON} />
				</div>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<h6 className="sub-header">Cultural influence</h6>
					<div className="qualities-result">
						{audience_primary_cultural_influence}
					</div>
				</div>
			</Row>
		) : null;

	const PrimaryGender = () =>
		audience_primary_gender && audience_primary_gender !== '' ? (
			<Row style={{ paddingBottom: '15px', paddingTop: '15px' }}>
				<div className="columns small-2 text-right">
					<img src={GENDER_ICON} />
				</div>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<h6 className="sub-header">Primary gender</h6>
					<div className="qualities-result">
						{audience_primary_gender}
					</div>
				</div>
			</Row>
		) : null;

	const AudienceSize = () =>
		audience_size && audience_size !== '' ? (
			<Row style={{ paddingBottom: '15px', paddingTop: '15px' }}>
				<div className="columns small-2 text-right">
					<img src={AUDIENCE_ICON} />
				</div>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<h6 className="sub-header">Audience size</h6>
					<div className="qualities-result">{audience_size}</div>
				</div>
			</Row>
		) : null;

	return (
		<>
			<AgeGroup />
			<CulturalInfluence />
			<PrimaryGender />
			<AudienceSize />
		</>
	);
};

export default Organization;
